import React from 'react';
import { Box } from '@chakra-ui/react';
import HeaderComponent from '../HeaderComponent';
import SubheaderComponent from '../SubheaderComponent';
import ParagraphComponent from '../ParagraphComponent';
import ImageComponent from '../ImageComponent';
import ProgressComponent from '../ProgressComponent';
import QuestionnaireComponent from '../QuestionnaireComponent';
import MultiQuestionnaireComponent from '../MultiQuestionnaireComponent';
import ListComponent from '../ListComponent';
import ContainerComponent from '../ContainerComponent';
import ModalComponent from '../ModalComponent';
import AccordionComponent from '../AccordionComponent';
import FormComponent from '../FormComponent';
import ContentDisplayComponent from '../ContentDisplayComponent';
import DrawingPadComponent from '../DrawingPadComponent';
import VideoComponent from '../VideoComponent';
import SpacerComponent from '../SpacerComponent';
import TitleComponent from '../TitleComponent';
import DynamicQuestionnaireComponent from '../DynamicQuestionnaireComponent';
import TabsComponent from '../TabsComponent';
import HealthScoreSummaryComponent from '../HealthScoreSummaryComponent';
import HealthMetricsComponent from '../HealthMetricsComponent';

/**
 * Dynamically resolves and renders UI components based on their type.
 * @param {Object} section - The section containing type and configuration for the component.
 * @param {string} theme - The current theme (e.g., "light" or "dark").
 * @param {Object} priorDecisions - Contextual decisions made prior to this component.
 * @param {Function} updateFormData - Callback to update form data.
 * @param {Function} handleButtonAction - Callback to handle button actions.
 * @returns {JSX.Element} The dynamically rendered component.
 *
 */
const ComponentFactory = ({
  section,
  theme,
  priorDecisions,
  formData,
  readOnly,
  updateFormData,
  handleButtonAction,
  isTouchDevice,
  isMouseDevice,
  onLongContentChange,
  onDynamicValidationChange
}) => {
  switch (section.type) {
    case 'title':
      return (
        <TitleComponent
          content={section.content}
          variant={section.variant || 'headline-md-max'}
          role={section.role || 'title'}
          ariaLabel={section.ariaLabel || `Title:${section.content}`}
          color={section.color}
          marginBottom={section.marginBottom} />
      );

    case 'header':
      return (
        <HeaderComponent
          content={section.content}
          variant={section.variant || 'headline-md-min'}
          ariaLabel={section.ariaLabel || `Header:${section.content}`}
          icon={section.icon}
          role={section.role || 'header'}
          color={section.color}
          textAlign={section.textAlign}
          marginBottom={section.marginBottom}
          marginTop={section.marginTop}
          listNumber={section.listNumber || null}
        />
      );

    case 'subheader':
      return (
        <SubheaderComponent
          content={section.content}
          marginBottom={section.marginBottom}
          variant={section.variant || 'body-md-min-reg'}
          ariaLabel={section.ariaLabel || `Header:${section.content}`}
          role={section.role || 'header'}
          color={section.color}
        />
      );

    case 'paragraph':
      return ( 
        <ParagraphComponent
          show={section.show}
          content={section.content}
          variant={section.variant || 'body-md-min-reg'}
          textAlign={section.textAlign || 'left'}
          marginBottom={section.marginBottom}
          ariaLabel={section.ariaLabel || `Paragraph:${section.content}`}
          role={section.role || 'paragraph'}
          color={section.color}
          links={section.links}
          handleButtonAction={handleButtonAction}
        />
      );

    case 'image':
      return (
        <Box mb={2} display={section.display}>
          <ImageComponent
            image={section.image}
            variant={section.variant}
            rotation={section.rotation}
            position={section.position}
            marginTop={section.marginTop}
            marginBottom={section.marginBottom}
          />
        </Box>
      );

    case 'video':
      return (
        <VideoComponent
          videoId={section.videoId}
          duration={section.duration}
          ariaLabel={section.ariaLabel}
          thumbnail={section.thumbnail}
        />
      );

    case 'accordion':
      return  <AccordionComponent items={section.items} />;

    case 'button':
      return null;
      
    case 'progress':
      return (
        <ProgressComponent
          current={section.current}
          total={section.total}
          title={section.title}
          variant={section.variant || 'sm'}
          showCount={section.showCount ?? true}
          showTitle={section.showTitle ?? true}
        />
      );

    case 'list':
      return (
        <ListComponent
          styleType={section.styleType || 'disc'}
          items={section.items || []}
          variant={section.variant || 'body-md-min-reg'}
          icon={section.icon || {}}
          image={section.image}
          markerColor={section.markerColor}
          marginTop={section.marginTop}
          marginBottom={section.marginBottom}
          itemColor={section.itemColor}
          startNumber={section.startNumber || 1}
        />
      );

    case 'container':
      return (
        <ContainerComponent
          content={section.content}
          variant={section.variant || 'container-body'}
          bgColor={section.bgColor}
          border={section.border}
          priorDecisions={priorDecisions}
          formData={formData}
          readOnly={readOnly}
          updateFormData={updateFormData}
          handleButtonAction={handleButtonAction}
          isTouchDevice={isTouchDevice}
          isMouseDevice={isMouseDevice}
        />
      );

    case 'modal':
      return (
        <ModalComponent
          size={section.size}
          isOpen={section.isOpen}
          onClose={section.onClose}
          modalContent={section.modalContent}
          handleButtonAction={handleButtonAction}
          closeButtonSize={section.closeButtonSize}
          closeButtonColor={section.closeButtonColor}
        />
      );

    case 'form':
      return (
        <FormComponent
          section={section}
          formData={formData}
          readOnly={readOnly}
          updateFormData={updateFormData}
          onSubmit={(btn) =>
            handleButtonAction(btn.action, btn)
          }
          isTouchDevice={isTouchDevice}
          isMouseDevice={isMouseDevice}
        />
      );

    case 'question':
      return (
        <Box height="100%" display="flex" flexDirection="column">
          <QuestionnaireComponent
            question={section}
            formData={formData}
            readOnly={readOnly}
            priorDecisions={priorDecisions}
            updateFormData={(key, value, label, metric, flowSheetId) => {
              updateFormData(key, value, label, metric, flowSheetId);
            }}
            handleButtonAction={handleButtonAction}
            isTouchDevice={isTouchDevice}
            isMouseDevice={isMouseDevice}
          />
        </Box>
      );

    case 'multi-question':
      return (
        <Box mb={6} height="100%">
          <MultiQuestionnaireComponent
            questions={section.questions}
            buttons={section.button}
            formData={formData}
            readOnly={readOnly}
            priorDecisions={priorDecisions}
            updateFormData={(key, value, label, metric, flowSheetId) => {
              updateFormData(key, value, label, metric, flowSheetId);
            }}
            handleButtonAction={handleButtonAction}
            isTouchDevice={isTouchDevice}
            isMouseDevice={isMouseDevice}
          />
        </Box>
      );

    case 'dynamic-question':
      return (
        <Box mb={6} height="100%">
          <DynamicQuestionnaireComponent
            section={section}
            formData={formData}
            readOnly={readOnly}
            priorDecisions={priorDecisions}
            updateFormData={(key, value, label, metric, flowSheetId) => {
              updateFormData(key, value, label, metric, flowSheetId);
            }}
            handleButtonAction={handleButtonAction}
            onSubmit={() =>
              handleButtonAction(section.button.action, section.button)
            }
            isTouchDevice={isTouchDevice}
            isMouseDevice={isMouseDevice}
            onLongContentChange={onLongContentChange}
            onDynamicValidationChange={onDynamicValidationChange}
          />
        </Box>
      );

    case 'content-list':
    case 'content-basic':
    case 'content-card':
      return (
        <ContentDisplayComponent type={section.type} items={section.items} marginBottom={section.marginBottom} marginTop={section.marginTop} />
      );

    case 'drawingPad':
      return (
        <Box>
        <DrawingPadComponent
          prompt={section.prompt || 'Draw a checkmark to confirm'}
          ariaLabel={section.ariaLabel || 'Drawing pad for showing interaction'}
          onConfirm={() => {
            handleButtonAction(section.action, { success: true });
          }}
          isDisabled={section.isDisabled || false}
          readOnly={readOnly}
          handleButtonAction={handleButtonAction}
          isTouchDevice={isTouchDevice}
          isMouseDevice={isMouseDevice}
        /></Box>
      );
      case "tabs":
        return (
          <TabsComponent
            content={section.content?.tabs || section.content || []}
            readOnly={readOnly}
            variant={section.variant || 'body-md-min-reg'}
            lineColor={section.lineColor || 'var(--sutter-sync-purple)'}
            handleButtonAction={handleButtonAction}
            isTouchDevice={isTouchDevice}
            isMouseDevice={isMouseDevice}
          />
        );

    case 'health-score-summary':
      return (
        <HealthScoreSummaryComponent
          header={section.header}
          overallScore={section.overallScore}
          scoreSummary={section.scoreSummary}
          actionPlan={section.actionPlan}
          marginTop={section.marginTop}
          marginBottom={section.marginBottom}
          containerVariant={section.containerVariant}
        />
      );

    case 'health-metrics':
      return (
        <HealthMetricsComponent
          metrics={section.metrics}
          marginTop={section.marginTop}
          marginBottom={section.marginBottom}
        />
      );

    case 'spacer':
      return  <SpacerComponent />;

    case 'background':
      return null;

    default:
      return (
        <p style={{ color: theme === 'light' ? 'red' : 'purple' }}>
          Unknown type: {section.type}
        </p>
      );
  }
};

export default ComponentFactory;
